import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, Subject, firstValueFrom, take, takeUntil } from 'rxjs';
import { Loader } from './shared/components/loader/loader.component';
import { CdnClientDirective } from './shared/directives/cdn-client.directive';
import { SmartLabelEnum } from './shared/enums/smartlabel.enum';
import { SmartLabelPipe } from './shared/pipes/smartlabel.pipe';
import { LoaderService } from './shared/services/loader.service';
import { OfflineService } from './shared/services/offline.service';
import { UserContextService } from './shared/services/user-context.service';
import { SmartLabelService } from './shared/services/smartlabel.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, CommonModule, CdnClientDirective, SmartLabelPipe, Loader],
    templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
    protected readonly copyrightYear = new Date().getFullYear();
    protected title: string = '';
    protected name!: Observable<string | null>;
    protected dropdownOpen: boolean = false;
    protected isOnline: boolean = true;

    private readonly smartLabels = {
        [SmartLabelEnum.MasterApplicationTitle]: 'Training Attendance App',
    };

    private readonly destroyRef = inject(DestroyRef);
    private readonly loaderService = inject(LoaderService);
    private readonly offlineService = inject(OfflineService);
    private readonly oidcSecurityService = inject(OidcSecurityService);
    private readonly smartLabelService = inject(SmartLabelService);
    private readonly titleService = inject(Title);
    private readonly unsubscribe = new Subject<void>();
    private readonly userContextService = inject(UserContextService);

    get isLoading(): Observable<boolean> {
        return this.loaderService.loading;
    }

    // Handle the logout button click event
    protected onLogoutButtonClicked = (): void => {
        this.oidcSecurityService.logoff().pipe(take(1)).subscribe();
        sessionStorage.clear();
    };

    /** Handle the profile name button click event. */
    protected onProfileClick = (): void => {
        // TODO: This does not do anything or go anywhere.
    };

    // Toggle the dropdown menu
    protected toggleDropdown = (event: Event): void => {
        event.preventDefault();
        this.dropdownOpen = !this.dropdownOpen;
    };

    /** Initializes smartlabel values with fallbacks and updates the browser title when the app-title label resolves. */
    private readonly initSmartLabels = (): void => {
        /** Set a fallback browser title immediately so the tab shows a sensible value before translated labels load. */
        this.titleService.setTitle(this.smartLabels[SmartLabelEnum.MasterApplicationTitle]);

        /** Re-type the entries so TypeScript treats each key as a valid smartlabels key. */
        const smartLabelEntries = Object.entries(this.smartLabels) as Array<[keyof typeof this.smartLabels, string]>;

        /** Load each smartlabel from the service and fall back to its default text when needed. */
        for (const [labelName, defaultText] of smartLabelEntries) {
            this.smartLabelService
                .getLabelText(labelName, { fallback: defaultText })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((translatedText: string) => {
                    this.smartLabels[labelName] = translatedText;

                    /** Update the browser title again when the translated application-title label is returned. */
                    if (labelName === SmartLabelEnum.MasterApplicationTitle) {
                        this.titleService.setTitle(translatedText);
                    }
                });
        }
    };

    /** Performs synchronous initialization logic. */
    private readonly onInitSync = (): void => {
        this.title = 'training-provider-mobile-app'; // Set the title of the component
        this.initSmartLabels();

        this.offlineService.isOnline$.pipe(takeUntil(this.unsubscribe)).subscribe({
            next: (value: boolean) => {
                this.isOnline = value;
            },
        });
    };

    /** Performs asynchronous initialization logic. */
    private readonly onInitAsync = async (): Promise<void> => {
        // Get the access token
        const token: string = await firstValueFrom(this.oidcSecurityService.getAccessToken());

        if (token?.trim()) {
            await this.userContextService.initialize(); // Ensure the name is set initially
        }

        this.name = this.userContextService.name$; // Set the name property to the user's name
    };

    // Initialize the component
    ngOnInit(): void {
        this.onInitSync();
        this.onInitAsync().finally(() => {
            /** Filler for when the async calls are done. */
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
